import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';
import Global from '../../../../store/global-selectors';
import { ActionableFilterPill } from '../../index';
import { useSelectedOrdersView } from '../../../../pages/orders-page/orders-left-nav/orders-left-nav-context';

const defaultStoreMessage = <FormattedMessage defaultMessage='Store' description='filterOptions.store' id='gwPKWT' />;
const multipleStoresMessage = (
  <FormattedMessage defaultMessage='(Multiple Stores)' description='filterOptions.multipleStores' id='ueLluZ' />
);

const getDisplayName = ({ appliedFilters, stores }) => {
  if (appliedFilters.length === 0) {
    return defaultStoreMessage;
  }
  if (appliedFilters.length > 1) {
    return multipleStoresMessage;
  }
  const [filter] = appliedFilters;
  const matchingStore = stores.find((store) => store.storeGuid === filter.criterionArgumentValue);
  if (!matchingStore) {
    return defaultStoreMessage;
  }
  return matchingStore.name;
};

const getStoreName = ({ stores, storeGuid }) => {
  const store = stores.find((s) => s.storeGuid === storeGuid);
  return store && store.name;
};

const storeNameSelector = createSelector((state) => state, getStoreName);

const displayNameSelector = createSelector((state) => state, getDisplayName);

const displayContentSelector = createSelector(
  (state) => state.isLockedToStoreStatus,
  storeNameSelector,
  displayNameSelector,
  (isLocked, storeName, displayName) => {
    return isLocked ? storeName : displayName;
  },
);

export const OrdersStorePillInnerComponent = React.forwardRef((props, ref) => {
  const { filterById = '' } = props;
  const stores = useSelector(Global.selectSellerActiveStores);
  const appliedFilters = useSelector((state) =>
    Global.selectOrdersFilteringActiveFilterViewFiltersById(state, {
      filterById,
    }),
  );
  const statusView = useSelectedOrdersView();
  const storeGuid = statusView?.storeGuid;
  const isLockedToStoreStatus = !!storeGuid;
  const displayContent = displayContentSelector({
    isLockedToStoreStatus,
    stores,
    storeGuid,
    appliedFilters,
  });

  return (
    <ActionableFilterPill
      {...props}
      ref={ref}
      displayContent={displayContent}
      filterById={filterById}
      isLocked={isLockedToStoreStatus}
      isSelected={!!appliedFilters.length}
    />
  );
});

OrdersStorePillInnerComponent.propTypes = {
  filterById: PropTypes.string,
};

export const OrdersStorePill = React.memo(OrdersStorePillInnerComponent);
